import { useEffect, useRef, useState } from 'react';

/**
 * SplitText — letter-by-letter reveal for headlines.
 * Inspired by reactbits.dev/text-animations/split-text.
 *
 * Props:
 *   text: string — primary text (required)
 *   accent: string — optional second segment rendered with the gradient
 *   stagger: number — ms delay between characters (default 22)
 *   delay: number — ms delay before animation starts (default 80)
 *   as: element tag (default 'span')
 *   startOnView: boolean — gate the animation on viewport entry instead of
 *     mount (default false). Required for any usage below the fold, otherwise
 *     the reveal finishes long before the user scrolls to it (gotcha #21).
 *
 * The gradient is applied to the accent wrapper (not each char), so it reads
 * as one continuous sweep across the accent text rather than restarting per
 * character. Animation is triggered on mount (or on viewport entry with
 * startOnView); prefers-reduced-motion is handled globally in globals.css.
 */
export default function SplitText({
  text,
  accent,
  stagger = 22,
  delay = 80,
  as: Tag = 'span',
  className = '',
  startOnView = false,
}) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!startOnView) {
      const t = setTimeout(() => setStarted(true), 30);
      return () => clearTimeout(t);
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [startOnView]);

  const renderSegment = (segment, segmentOffset) => {
    const words = segment.split(' ');
    let charIndex = segmentOffset;
    return words.map((word, wi) => {
      const chars = [...word];
      return (
        <span key={wi}>
          <span className="split-word">
            {chars.map((ch, ci) => {
              const i = charIndex++;
              return (
                <span
                  key={ci}
                  className="split-char"
                  style={{ animationDelay: `${delay + i * stagger}ms` }}
                >
                  {ch}
                </span>
              );
            })}
          </span>
          {wi < words.length - 1 && ' '}
        </span>
      );
    });
  };

  const primaryLen = text.replace(/\s/g, '').length;

  return (
    <Tag ref={ref} className={`${started ? 'split-in' : ''} ${className}`}>
      {renderSegment(text, 0)}
      {accent && (
        <>
          {' '}
          <span className="text-gradient-accent">{renderSegment(accent, primaryLen)}</span>
        </>
      )}
    </Tag>
  );
}
